// Yönetim › Finansal — kliniğin token bakiyesi, tüketimi ve satın alımları.
// Analiz dili (docs/design-principles.md §4): üstte KPI satırı, altında sabit
// gövde yükseklikli kartlar. Renk yalnız hareketin YÖNÜNÜ anlatır:
// mavi giriş (satın alım), kırmızı çıkış (tüketim) — tokenChartTheme.js.
import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Typography, Skeleton, alpha } from "@mui/material";
import {
  ReceiptLong as ReceiptIcon,
  BarChart as BarChartIcon,
  DonutSmall as BreakdownIcon,
  Toll as TokenIcon,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { useLazyGetPerformanceFinancialQuery } from "../../store/dashboardApi";
import { COLORS, TYPO, CARD_GAP } from "../../styles/uiConstants";
import { getOperationLabel } from "../../utils/operationLabels";
import KpiTile from "../../components/molecules/KpiTile";
import {
  AXIS_TICK,
  GRID,
  ChartTip,
  RankBarList,
  BodySkeleton,
  CardEmpty,
  AnalyticsCard,
  nf,
} from "../../components/organisms/charts/analyticsParts";
import { FLOW_COLORS, formatToken } from "../../components/organisms/charts/tokenChartTheme";

/** Bakiye bu kadar günden az yetecekse "azalıyor" uyarısı. */
const LOW_RUNWAY_DAYS = 14;

/** Grafik ve liste kartlarının sabit gövde yükseklikleri (§4). */
const TOP_BODY = 360;
const BILLS_BODY = 320;

const EMPTY_RESULT = {
  balance: 0,
  periodConsumption: 0,
  periodTransactions: 0,
  weeklyConsumption: 0,
  weeklyAvgDailyCost: 0,
  byOperation: [],
  monthlyTrend: [],
  billsList: [],
};

/**
 * Yalnız geliştirmede (npm start) durum önizlemesi: URL'ye `&onizleme=` ekle.
 *   bos        → hiçbir kartta veri yok (kart içi boş durumlar)
 *   yukleniyor → yükleme iskeleti
 * Production build'de yok sayılır.
 */
const PREVIEWS = { bos: EMPTY_RESULT };

function formatTRDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

const formatTL = (n) =>
  Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const scrollAreaSx = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overscrollBehavior: "contain",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": { bgcolor: COLORS.slate200, borderRadius: 3 },
};

/** Fatura satırının sağ sütunları: sağa hizalı, tabular rakam. */
const figureSx = {
  width: { xs: 96, sm: 140 },
  flexShrink: 0,
  textAlign: "right",
  fontSize: TYPO.body,
  fontWeight: 500,
  color: COLORS.slate800,
  fontVariantNumeric: "tabular-nums",
};

const captionSx = {
  fontSize: TYPO.caption,
  fontWeight: 400,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: COLORS.slate500,
};

/** Satın alım kayıtları: kart içinde kayan düz satır listesi + sabit toplam satırı. */
function BillsList({ bills, loading }) {
  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, pt: 0.5 }}>
        {[...Array(6)].map((_, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Skeleton width={`${30 + ((i * 13) % 25)}%`} />
              <Skeleton width={80} sx={{ fontSize: TYPO.caption }} />
            </Box>
            <Skeleton width={70} />
            <Skeleton width={90} />
          </Box>
        ))}
      </Box>
    );
  }

  if (!bills.length) {
    return (
      <CardEmpty
        icon={<ReceiptIcon />}
        title="Henüz satın alım yok"
        description="Kliniğe faturayla token yüklendikçe kayıtlar burada listelenir."
      />
    );
  }

  const tokenTotal = bills.reduce((s, b) => s + Number(b.tokens || 0), 0);
  const priceTotal = bills.reduce((s, b) => s + Number(b.totalIncome || 0), 0);

  return (
    <>
      {/* Sütun başlıkları */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pb: 1,
          borderBottom: `1px solid ${COLORS.slate100}`,
        }}
      >
        <Typography sx={{ ...captionSx, flex: 1 }}>Fatura</Typography>
        <Typography sx={{ ...figureSx, ...captionSx }}>
          Yüklenen token
        </Typography>
        <Typography sx={{ ...figureSx, ...captionSx }}>
          Tutar (₺)
        </Typography>
      </Box>

      <Box sx={scrollAreaSx}>
        {bills.map((bill, idx) => (
          <Box
            key={bill._id || idx}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              py: 1.25,
              borderBottom: `1px solid ${COLORS.slate100}`,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: TYPO.body, fontWeight: 500, color: COLORS.slate700 }}>
                {bill.billNo || "Numarasız fatura"}
              </Typography>
              <Typography noWrap sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>
                {formatTRDate(bill.createdAt)}
                {bill.firmApellation ? ` · ${bill.firmApellation}` : ""}
              </Typography>
            </Box>
            <Box sx={{ ...figureSx, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.75 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "2px", bgcolor: FLOW_COLORS.in }} />+
              {formatToken(bill.tokens)}
            </Box>
            <Typography sx={figureSx}>
              {bill.totalIncome != null ? formatTL(bill.totalIncome) : "—"}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Toplam — listenin altında sabit */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pt: 1.25,
          borderTop: `1px solid ${COLORS.slate200}`,
        }}
      >
        <Typography sx={{ flex: 1, fontSize: TYPO.body, color: COLORS.slate600 }}>
          Toplam · {nf(bills.length)} fatura
        </Typography>
        <Typography sx={{ ...figureSx, fontWeight: 600 }}>+{formatToken(tokenTotal)}</Typography>
        <Typography sx={{ ...figureSx, fontWeight: 600 }}>{formatTL(priceTotal)}</Typography>
      </Box>
    </>
  );
}

export default function FinancialTab({ token, period: externalPeriod }) {
  // Periyot toggle'ı YOK — her bölüm kendi sabit penceresine bağlı:
  //   • Bakiye → anlık • Bu ay harcanan → içinde bulunulan ay
  //   • Günlük ort. → son 7 gün • Grafik → son 3 ay
  //   • İşlem dağılımı / faturalar → sunucunun sabit aralığı
  const fetchPeriod = externalPeriod || "this_month";

  const [fetch, { data, isLoading }] = useLazyGetPerformanceFinancialQuery();

  useEffect(() => {
    if (token) fetch({ token, period: fetchPeriod });
  }, [token, fetchPeriod, fetch]);

  const [searchParams] = useSearchParams();
  const preview = process.env.NODE_ENV === "development" ? searchParams.get("onizleme") : null;

  const loading = preview === "yukleniyor" || (!PREVIEWS[preview] && (isLoading || !data));
  const result = PREVIEWS[preview] || data?.data || EMPTY_RESULT;

  const balance = Number(result.balance ?? 0);
  const periodConsumption = Number(result.periodConsumption ?? 0);
  const periodTransactions = Number(result.periodTransactions ?? 0);
  const weeklyConsumption = Number(result.weeklyConsumption ?? result.periodConsumption ?? 0);
  const avgDailyCost = Number(result.weeklyAvgDailyCost ?? result.avgDailyCost ?? 0);
  const monthlyTrend = result.monthlyTrend || [];
  const billsList = result.billsList || [];
  const lifetimeBills = result.bills?.lifetime;

  const operations = (result.byOperation || result.topOperations || []).map((op) => ({
    name: getOperationLabel(op.operation || op.name) || "Diğer",
    count: Number(op.total ?? op.totalTokens ?? 0),
  }));
  const opTotal = operations.reduce((s, op) => s + op.count, 0);
  const trendTotal = monthlyTrend.reduce((s, m) => s + Number(m.total || 0), 0);

  // Mevcut hızla bakiye kaç gün yeter
  const runwayDays = avgDailyCost > 0 ? Math.floor(balance / avgDailyCost) : null;
  const isLow = runwayDays !== null && runwayDays < LOW_RUNWAY_DAYS;

  const purchasedTokens = Number(
    lifetimeBills?.tokens ?? billsList.reduce((s, b) => s + Number(b.tokens || 0), 0),
  );
  const purchasedCount = Number(lifetimeBills?.count ?? billsList.length);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: CARD_GAP }}>
      {/* Özet — stok (bakiye) önce, akışlar sonra */}
      <Box
        sx={{
          display: "grid",
          gap: CARD_GAP,
          gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" },
        }}
      >
        <KpiTile
          loading={loading}
          label="Kalan token"
          value={formatToken(balance)}
          valueColor={isLow ? COLORS.warningDarker : undefined}
          hint={
            runwayDays === null
              ? "Son 7 günde tüketim yok"
              : `${isLow ? "Azalıyor · " : ""}Mevcut tüketimle ~${nf(runwayDays)} gün yeter`
          }
        />
        <KpiTile
          loading={loading}
          label="Bu ay harcanan"
          value={formatToken(periodConsumption)}
          hint={periodTransactions ? `${nf(periodTransactions)} işlemde tüketildi` : "Bu ay tüketim yok"}
        />
        <KpiTile
          loading={loading}
          label="Günlük ortalama"
          value={avgDailyCost.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}
          hint={`Son 7 günde ${formatToken(weeklyConsumption)} token`}
        />
        <KpiTile
          loading={loading}
          label="Satın alınan"
          value={formatToken(purchasedTokens)}
          hint={purchasedCount ? `Toplam ${nf(purchasedCount)} faturayla` : "Henüz fatura yok"}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: CARD_GAP,
          gridTemplateColumns: { xs: "1fr", lg: "1.4fr 1fr" },
        }}
      >
        <AnalyticsCard
          title="Aylık token tüketimi"
          subtitle="Son 3 ayda harcanan token"
          icon={<BarChartIcon />}
          bodyHeight={TOP_BODY}
        >
          {loading ? (
            <BodySkeleton kind="bars" />
          ) : !trendTotal ? (
            <CardEmpty
              icon={<BarChartIcon />}
              title="Son 3 ayda tüketim yok"
              description="Klinikte token harcayan işlemler yapıldıkça aylık tüketim burada görünür."
            />
          ) : (
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyTrend} barSize={24} margin={{ top: 8, right: 4, left: -8, bottom: 0 }}>
                  <CartesianGrid {...GRID} />
                  <XAxis
                    dataKey="label"
                    tick={AXIS_TICK}
                    axisLine={{ stroke: COLORS.slate200 }}
                    tickLine={false}
                    interval={0}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={AXIS_TICK}
                    tickFormatter={(v) => nf(v)}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: alpha(COLORS.slate200, 0.4) }}
                    content={
                      <ChartTip
                        format={(p) =>
                          `${formatToken(p.value)} token · ${nf(p.payload?.count)} işlem`
                        }
                      />
                    }
                  />
                  <Bar dataKey="total" name="Tüketim" fill={FLOW_COLORS.out} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </AnalyticsCard>

        <AnalyticsCard
          title="Token nereye harcanıyor?"
          subtitle="En çok token harcayan işlemler"
          icon={<BreakdownIcon />}
          bodyHeight={TOP_BODY}
        >
          {loading ? (
            <BodySkeleton kind="list" />
          ) : (
            <RankBarList
              items={operations}
              color={FLOW_COLORS.out}
              total={opTotal}
              unit="token"
              emptyText={{
                icon: <TokenIcon />,
                title: "Henüz harcama yok",
                description: "Token harcayan işlemler yapıldıkça en çok harcayanlar burada sıralanır.",
              }}
            />
          )}
        </AnalyticsCard>
      </Box>

      <AnalyticsCard
        title="Token satın alımları"
        subtitle="Faturayla yüklenen son token kayıtları"
        icon={<ReceiptIcon />}
        bodyHeight={BILLS_BODY}
      >
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <BillsList bills={billsList} loading={loading} />
        </Box>
      </AnalyticsCard>
    </Box>
  );
}
